import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Animal, Insurance, State } from "../state/types";
import { getDefaultAnimal, getGermanHealthInsurance } from "../state/utils";
import {
  MONTHLY_BILLING_INTERVAL,
  YEARLY_BILLING_INTERVAL,
} from "utils/insurance";
import { UpsertDraft_upsertDraft_policyPreviews } from "../../../../../__generated__/UpsertDraft";
import { isOrg } from "utils/site";
import { updateApplyPreventiveAddonToAll } from "./metadata";

export const updateInsuranceById = createAction(
  "animals/updateInsuranceById",
  (
    payload: { id: string; update: Partial<Insurance> },
    meta?: { refetch: boolean }
  ) => {
    return {
      payload,
      meta,
    };
  }
);

export const switchAnimal = createAction(
  "animals/switchAnimal",
  (payload: string) => {
    return { payload };
  }
);

export const upsertDraft = createAction(
  "animals/upsertDraft",
  (payload: undefined, meta?: { navigateToNextOnSuccess?: boolean }) => {
    return { payload, meta };
  }
);

export const editAnimalById = createAction(
  "animals/editAnimalById",
  (payload: string) => {
    return { payload };
  }
);

export const editDraftsByAnimalId = createAction(
  "animals/editDraftsByAnimalId",
  (payload: string) => {
    return { payload };
  }
);

export const upsertDraftFailure = createAction(
  "animals/upsertDraftFailure",
  (payload: Error) => {
    return { payload };
  }
);

export const createQuote = createAction("animals/createQuote");

export const animalSlice = createSlice({
  name: "animals",
  initialState: {} as State["animals"],
  reducers: {
    updateAnimalById: (
      state,
      action: PayloadAction<{ id: string; update: Partial<Animal> }>
    ) => {
      return state.map((pet) => {
        if (action.payload.id === pet.id) {
          return {
            ...pet,
            ...action.payload.update,
          };
        }

        return pet;
      });
    },
    commitAnimalById: (state, action: PayloadAction<string>) => {
      return state.map((animal) => {
        if (animal.id === action.payload) {
          return {
            ...animal,
            isDraft: false,
          };
        }
        return animal;
      });
    },
    addAnimal: (state) => {
      const validAnimals = state.filter((animal) => !animal.isDraft);

      if (isOrg("ger") && state.length >= 1) {
        return [...validAnimals, getDefaultAnimal(getGermanHealthInsurance())];
      }

      return [...validAnimals, getDefaultAnimal()];
    },
    removeAnimalById: (state, action: PayloadAction<string>) => {
      return state.filter((pet) => pet.id !== action.payload);
    },
    upsertDraftSuccess: (
      state,
      action: PayloadAction<{
        clientName: string | null;
        clientId: string | null;
        draftId: string;
        drafts: (UpsertDraft_upsertDraft_policyPreviews | null)[];
      }>
    ) => {
      return state.map((pet) => ({
        ...pet,
        insurances: pet.insurances.map((insurance) => {
          const newDrafts = action.payload.drafts.find(
            (draft) => draft?._id === insurance.id
          );

          if (newDrafts) {
            return {
              ...insurance,
              insurely: {
                ...insurance.insurely,
                clientName: action.payload.clientName,
              },
              drafts: newDrafts.plans,
            };
          }

          return insurance;
        }),
      }));
    },
    updatePreventiveAddonLimitOnAll: (state, action: PayloadAction<number>) => {
      return state.map((pet) => {
        return {
          ...pet,
          insurances: pet.insurances.map((insurance) => ({
            ...insurance,
            preventiveCareAddOnLimit: action.payload,
          })),
        };
      });
    },

    updateBillingInterval: (
      state,
      action: PayloadAction<
        typeof YEARLY_BILLING_INTERVAL | typeof MONTHLY_BILLING_INTERVAL
      >
    ) => {
      return state.map((pet) => {
        return {
          ...pet,
          insurances: pet.insurances.map((insurance) => ({
            ...insurance,
            billingInterval: action.payload,
          })),
        };
      });
    },

    updateBillingDay: (state, action: PayloadAction<number>) => {
      return state.map((pet) => {
        return {
          ...pet,
          insurances: pet.insurances.map((insurance) => ({
            ...insurance,
            billingDay: action.payload,
          })),
        };
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateInsuranceById, (state, action) => {
      return state.map((pet) => {
        return {
          ...pet,
          insurances: pet.insurances.map((insurance) => {
            if (action.payload.id === insurance.id) {
              return {
                ...insurance,
                ...action.payload.update,
              };
            }

            return insurance;
          }),
        };
      });
    });

    builder.addCase(updateApplyPreventiveAddonToAll, (state, action) => {
      if (action.payload.active) {
        return state.map((pet) => {
          return {
            ...pet,
            insurances: pet.insurances.map((insurance) => ({
              ...insurance,
              preventiveCareAddOnLimit: action.payload.limit,
            })),
          };
        });
      }

      return state;
    });
  },
});

export const {
  updateAnimalById,
  updateBillingInterval,
  updatePreventiveAddonLimitOnAll,
  updateBillingDay,
  addAnimal,
  removeAnimalById,
  upsertDraftSuccess,
  commitAnimalById,
} = animalSlice.actions;

export type AnimalsActions = typeof animalSlice.actions;

export const animalsReducer = animalSlice.reducer;
